import React, {useState} from "react";
import {Formik} from "formik";
import {
  Button,
  BlockField,
  ModalFooterButtons,
  SlideBlock,
  ExternalLink,
  Text,
} from "@reside/ui";
import {Blocks} from "@reside/forms";

import {ScrollContextProvider, useScrollContext} from "../scroll-context";
import {getPdfDocumentName} from "./helpers";
import {ScrollPdfToBottomReminder} from "./ScrollPdfToBottomReminder";
import {
  AdmissionsService,
  downloadWorkflowPdf,
  hasArbitrationDocument,
} from "../../../services/AdmissionsService";
import {useDownloadManager} from "../../../hooks/useDownloadManager";
import {FullHeightColumn, SignAdmissionFooter} from "./styles";
import {DefaultSignOverlay} from "./DefaultSignOverlay";
import {PDFViewer} from "../../inline-pdf-viewer";
import {Draft} from "../model";
import {useAdmissionContext} from "../AdmissionContext";
import {GeneratingPdfLoader} from "./GeneratingPdfLoader";
import {useTimeoutFn} from "react-use";
import {
  getWorkflowLabel,
  getWorkflowPacketLabel,
} from "../../start-workflow/workflowMapping";

type Props = Readonly<{
  draft: Draft;
  isSigning: boolean;
  onRequestClose: () => void;
  signAdmission: () => void;
  signOverlay: SlideBlock;
}>;

const SignOverlayContent = ({
  draft,
  isSigning,
  onRequestClose,
  signAdmission,
  signOverlay,
  scrollRef,
}: Props & {scrollRef: React.RefObject<HTMLDivElement>}) => {
  const {scrolledToBottomBefore} = useScrollContext();
  const {start} = useDownloadManager();

  // Get PDF state from context
  const {pdfFile, isPdfGenerating, hasScrollEnforcementFeatureFlag} =
    useAdmissionContext();

  const workflowLabel = getWorkflowLabel(draft.instance.workflowType);
  const downloadPacketLabel = `Download ${getWorkflowPacketLabel(
    workflowLabel,
  )}`;

  const [pdfPreviewRecorded, setPdfPreviewRecorded] = useState(false);

  const recordPdfPreviewLoaded = () => {
    if (!pdfPreviewRecorded) {
      setPdfPreviewRecorded(true);
      AdmissionsService.patch(draft.instance.id, {
        pdfPreviewLoadedTimestamp: new Date().toISOString(),
      });
    }
  };

  const [previewDisabled, setPreviewDisabled] = useState(false);
  const [, , reset] = useTimeoutFn(() => {
    setPreviewDisabled(false);
  }, 5000);

  const disablePreview = () => {
    setPreviewDisabled(true);
    reset();
  };

  return (
    <>
      <div
        ref={scrollRef}
        style={{
          height: "calc(100% - 124px)",
          overflow: "auto",
          pointerEvents:
            hasScrollEnforcementFeatureFlag && isPdfGenerating
              ? "none"
              : "auto",
        }}
      >
        <FullHeightColumn>
          <Formik initialValues={{}} onSubmit={() => {}}>
            {/* skipcq: JS-0424 - this fragment is required so that single child is passed to Formik. Removing this will cause issue DEV-3756. */}
            <>
              {signOverlay ? (
                signOverlay.children.map((block: Blocks) => (
                  <BlockField key={block.id} block={block} />
                ))
              ) : (
                <DefaultSignOverlay workflowName={workflowLabel} />
              )}
            </>
          </Formik>
          {isPdfGenerating ? (
            <GeneratingPdfLoader />
          ) : hasScrollEnforcementFeatureFlag ? (
            <PDFViewer
              pdfDocumentName={getPdfDocumentName(draft)}
              workflowLabel={workflowLabel}
              customOnLoadSuccess={() => {
                recordPdfPreviewLoaded();
              }}
              file={pdfFile}
            />
          ) : (
            <Text fontSize={[3, 4]}>
              <ExternalLink
                disabled={previewDisabled}
                onClick={() => {
                  disablePreview();
                  start(downloadPacketLabel, () =>
                    downloadWorkflowPdf({
                      workflowId: draft.instance.id,
                      type: "full",
                      status: draft.instance.status,
                    }),
                  );
                }}
              >
                {downloadPacketLabel}.
              </ExternalLink>
            </Text>
          )}
          {!isPdfGenerating &&
            hasArbitrationDocument(draft.instance.templateDocumentTypes) && (
              <Text as="p" fontSize={[3, 4]} marginTop={[2, 3]}>
                <ExternalLink
                  onClick={() => {
                    start("Download Arbitration Agreement", () =>
                      downloadWorkflowPdf({
                        workflowId: draft.instance.id,
                        type: "arbitration",
                        status: draft.instance.status,
                      }),
                    );
                  }}
                >
                  Download Arbitration Agreement
                </ExternalLink>
              </Text>
            )}
        </FullHeightColumn>
      </div>

      {hasScrollEnforcementFeatureFlag && !isPdfGenerating && (
        <ScrollPdfToBottomReminder isLoadingPdf={isPdfGenerating} />
      )}

      <ModalFooterButtons>
        <SignAdmissionFooter
          hasShadow
          left={
            <Button
              testId="sign-refuse-button"
              onClick={onRequestClose}
              outline
              size="large"
            >
              Close
            </Button>
          }
          right={
            <Button
              testId="sign-confirm-button"
              color="primary"
              disabled={
                isSigning ||
                (hasScrollEnforcementFeatureFlag && !scrolledToBottomBefore) ||
                isPdfGenerating
              }
              onClick={() => {
                signAdmission();
              }}
              size="large"
            >
              {isSigning ? "Signing..." : "Confirm"}
            </Button>
          }
        />
      </ModalFooterButtons>
    </>
  );
};

export const SignOverlay = (props: Props) => {
  return (
    <ScrollContextProvider bottomTolerance={25}>
      {scrollRef => <SignOverlayContent {...props} scrollRef={scrollRef} />}
    </ScrollContextProvider>
  );
};
